import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setInfluencerChannel } from '../../features/ui/uiSlice';
import {
  usePayoutQuery, useCreateInfluencerMutation, useUpdateInfluencerMutation, useDeleteInfluencerMutation,
} from '../../services/api';
import { CH, CHMAP, CHCOLOR } from '../../constants/channels';
import { slabCalc, validateSlabs, DEFAULT_SLABS } from '../../utils/slabs';
import { inr, inrShort, pct, fmtPct } from '../../utils/format';
import { KpiGrid } from '../../components/common/KpiCard';
import DataCard from '../../components/common/DataCard';
import AppButton from '../../components/common/AppButton';
import QueryState from '../../components/common/QueryState';
import { KpiSkeleton, TierCardSkeleton, ControlsSkeleton } from '../../components/common/Shimmer';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../components/common/Toast';
import { ReadOnlyNotice } from '../../components/common/Access';
import { usePermissions } from '../../features/auth/permissions';
import { SectionHeading, NotePanel, Badge } from '../../components/common/Bits';

/** Long enough that a typed rupee figure is one request, short enough to feel saved. */
const SAVE_DELAY = 700;

const CELL_INPUT_SX = {
  width: '100%',
  border: '1px solid var(--border)',
  borderRadius: '7px',
  p: '6px 8px',
  background: 'var(--card-2)',
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--ink)',
  fontSize: 12.5,
  '& input': { p: 0, textAlign: 'right' },
  '&.Mui-focused': { outline: '2px solid var(--mint)', background: 'var(--card)' },
};

function ChannelPickerChip({ channel, active, onClick, revenue }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        p: '7px 11px',
        border: '1px solid',
        borderColor: active ? 'var(--mint)' : 'var(--border)',
        borderRadius: '999px',
        cursor: 'pointer',
        fontSize: 12.5,
        background: active ? '#eaf7f0' : 'var(--card-2)',
      }}
    >
      <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', background: CHCOLOR[channel.id] }} />
      {channel.name}
      <Badge sx={{ ml: 0.5 }}>{revenue}</Badge>
    </Box>
  );
}

/**
 * One partner's tier ladder.
 *
 * Typing edits a local draft and previews the payout with the same maths the server
 * runs, then flushes one PATCH after a pause — a per-keystroke write would put a
 * request behind every digit of "300000". Structural changes (add/remove a tier)
 * save immediately, since there is nothing more to type.
 */
function InfluencerCard({ channelId, influencer, channelRevenue, onSave, onRemove, canEdit }) {
  const [draft, setDraft] = useState(null);
  const timer = useRef(null);
  // Every edit takes a number; a save reports the number it carried. While the two
  // differ there is typing the server hasn't seen, so the draft must stand.
  const edits = useRef(0);
  const saved = useRef(0);
  const id = influencer.id;

  // The server is the source of truth between edits; a draft only exists mid-edit.
  const current = draft || {
    name: influencer.name,
    baseOverride: influencer.baseOverride,
    slabs: influencer.slabs,
  };

  // Once every edit has been saved the server's copy is authoritative — and it may
  // differ from what was typed, since it sorts the tiers. Drop the draft so that
  // version shows. Doing this while an edit is still outstanding would rewrite the
  // field mid-keystroke, and the next digit would land on the reverted value.
  useEffect(() => {
    if (saved.current === edits.current) setDraft(null);
  }, [influencer]);

  // A queued save must not fire after the card is gone (channel switch, delete).
  useEffect(() => () => clearTimeout(timer.current), []);

  const commit = async (next, seq) => {
    try {
      await onSave(id, next);
    } finally {
      saved.current = Math.max(saved.current, seq);
    }
  };

  const edit = (changes) => {
    const next = { ...current, ...changes };
    const seq = ++edits.current;
    setDraft(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => commit(next, seq), SAVE_DELAY);
  };

  /** Structural edits are worth saving at once — there is no follow-up keystroke. */
  const editNow = (changes) => {
    const next = { ...current, ...changes };
    const seq = ++edits.current;
    setDraft(next);
    clearTimeout(timer.current);
    commit(next, seq);
  };

  const setSlab = (i, field, value) =>
    edit({ slabs: current.slabs.map((s, j) => (i === j ? { ...s, [field]: value } : s)) });

  const addTier = () => {
    const slabs = current.slabs;
    const last = slabs[slabs.length - 1];
    const prev = slabs.length > 1 ? +slabs[slabs.length - 2].upto || 0 : 0;
    const nextUpto = last.upto == null ? prev + 100000 : +last.upto + 100000;
    editNow({ slabs: [...slabs.slice(0, -1), { upto: nextUpto, rate: last.rate }, last] });
  };

  const base = current.baseOverride != null ? current.baseOverride : channelRevenue;
  const calc = slabCalc(base, current.slabs);
  const problem = validateSlabs(current.slabs);

  let lo = 0;
  const rows = current.slabs.map((s, i) => {
    const from = lo;
    lo = s.upto == null || s.upto === '' ? lo : +s.upto;
    // parts come back in normalized order, so match on the band's start, not the index
    const part = calc.parts.find((pt) => pt.from === from) || { amt: 0, pay: 0 };
    return { s, i, isLast: i === current.slabs.length - 1, part, from };
  });

  return (
    <Paper
      sx={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow)',
        p: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
        <Box sx={{ width: 11, height: 11, borderRadius: '50%', background: CHCOLOR[channelId], flex: 'none' }} />
        <InputBase
          value={current.name}
          onChange={(e) => edit({ name: e.target.value })}
          sx={{
            flex: 1,
            fontFamily: 'var(--disp)',
            fontWeight: 600,
            fontSize: 16,
            border: '1px solid transparent',
            borderRadius: '8px',
            p: '5px 7px',
            '&:hover': { borderColor: 'var(--border)' },
            '&.Mui-focused': { borderColor: 'var(--mint)', background: 'var(--card-2)' },
          }}
        />
        {canEdit && <AppButton mini tone="danger" onClick={() => onRemove(influencer)}>Remove</AppButton>}
      </Box>

      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
          background: 'var(--card-2)', border: '1px solid var(--border)', borderRadius: '10px', p: '10px 12px',
        }}
      >
        <Box sx={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          Attributed revenue
        </Box>
        <InputBase
          type="number"
          inputProps={{ min: 0 }}
          value={current.baseOverride != null ? current.baseOverride : ''}
          placeholder={String(Math.round(channelRevenue))}
          onChange={(e) => edit({ baseOverride: e.target.value === '' ? null : Math.max(0, +e.target.value) })}
          sx={{ ...CELL_INPUT_SX, width: 130, background: 'var(--card)' }}
        />
        <AppButton
          mini
          disabled={current.baseOverride == null}
          onClick={() => editNow({ baseOverride: null })}
        >
          {current.baseOverride == null ? 'Auto (channel rev)' : 'Reset to channel rev'}
        </AppButton>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['From', 'Up to (₹)', 'Rate %', 'In-band', 'Payout', ''].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: 'right', borderBottom: '1px solid var(--grid)', padding: '6px 8px',
                    fontFamily: 'var(--mono)', fontSize: 10.5, textTransform: 'uppercase',
                    color: 'var(--ink-2)', letterSpacing: '.05em', fontWeight: 500,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ s, i, isLast, part, from }) => (
              <tr key={i}>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--grid)', textAlign: 'right', color: 'var(--ink-2)', fontVariantNumeric: 'tabular-nums' }}>
                  {inr(from)}
                </td>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--grid)' }}>
                  <InputBase
                    type="number"
                    inputProps={{ min: 0 }}
                    value={s.upto == null ? '' : s.upto}
                    placeholder={isLast ? '∞' : ''}
                    onChange={(e) => setSlab(i, 'upto', e.target.value === '' ? null : Math.max(0, +e.target.value))}
                    sx={CELL_INPUT_SX}
                  />
                </td>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--grid)', width: 74 }}>
                  <InputBase
                    type="number"
                    inputProps={{ min: 0, step: 0.5 }}
                    value={s.rate}
                    onChange={(e) => setSlab(i, 'rate', Math.max(0, +e.target.value || 0))}
                    sx={CELL_INPUT_SX}
                  />
                </td>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--grid)', textAlign: 'right', color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
                  {inrShort(part.amt)}
                </td>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--grid)', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {inr(part.pay)}
                </td>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--grid)', textAlign: 'right' }}>
                  {current.slabs.length > 1 && (
                    <AppButton
                      mini
                      tone="danger"
                      onClick={() => editNow({ slabs: current.slabs.filter((_, j) => j !== i) })}
                    >
                      ✕
                    </AppButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {canEdit && <AppButton mini sx={{ alignSelf: 'flex-start' }} onClick={addTier}>＋ Add tier</AppButton>}

      {problem && (
        <Box sx={{ fontSize: 12, color: 'var(--crit)', background: '#fdeaea', border: '1px solid #f3c9c9', borderRadius: '8px', p: '8px 11px' }}>
          {problem} — not saved yet.
        </Box>
      )}
      {!problem && calc.uncovered > 0 && (
        <Box sx={{ fontSize: 12, color: 'var(--ink-2)', background: 'var(--card-2)', border: '1px solid var(--border)', borderRadius: '8px', p: '8px 11px' }}>
          {inr(calc.uncovered)} of the base sits above the top tier and earns nothing. Leave the last “Up to” blank for
          “and above”.
        </Box>
      )}

      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          p: '11px 13px', background: '#eef8f2', border: '1px solid #cdead9', borderRadius: '10px',
        }}
      >
        <Box>
          <Box sx={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--tealdim)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Total commission on {inrShort(base)}
          </Box>
          <Box sx={{ fontFamily: 'var(--disp)', fontWeight: 700, fontSize: 20, color: 'var(--side)' }}>{inr(calc.total)}</Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Box sx={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--tealdim)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Effective rate
          </Box>
          <Box sx={{ fontFamily: 'var(--disp)', fontWeight: 700, fontSize: 16, color: 'var(--side)' }}>{fmtPct(calc.eff)}</Box>
        </Box>
      </Box>
    </Paper>
  );
}

/** Channel chips, the KPI band, then two tier editors side by side. */
function InfluencersSkeleton() {
  return (
    <>
      <ControlsSkeleton count={5} />
      <KpiSkeleton columns={4} />
      <Grid container spacing={2}>
        {[0, 1].map((i) => (
          <Grid key={i} size={{ xs: 12, lg: 6 }}><TierCardSkeleton /></Grid>
        ))}
      </Grid>
    </>
  );
}

export default function InfluencersPage() {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { canEdit, role } = usePermissions();
  const { range, influencerChannel } = useAppSelector((s) => s.ui);
  const channelId = CHMAP[influencerChannel] ? influencerChannel : CH[0].id;

  // One request covers the picker chips and the open channel — the payout endpoint
  // returns every channel the caller can reach, revenue and computed tiers together.
  const payoutQuery = usePayoutQuery({ range });
  const [createInfluencer] = useCreateInfluencerMutation();
  const [updateInfluencer] = useUpdateInfluencerMutation();
  const [deleteInfluencer] = useDeleteInfluencerMutation();
  const [confirm, setConfirm] = useState(null);

  const fail = (e, fallback) => toast(e?.data?.error || e?.message || fallback, 'error');

  const save = useCallback(
    async (id, next) => {
      // never send a ladder the server will reject — the card is already saying why
      if (validateSlabs(next.slabs)) return;
      try {
        await updateInfluencer({
          id,
          name: next.name,
          baseOverride: next.baseOverride,
          slabs: next.slabs.map((s) => ({ upto: s.upto === '' ? null : s.upto, rate: +s.rate || 0 })),
        }).unwrap();
      } catch (e) {
        fail(e, "Couldn't save those tiers");
      }
    },
    [updateInfluencer] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const add = async () => {
    try {
      await createInfluencer({ channel: channelId, name: 'New Influencer', slabs: DEFAULT_SLABS() }).unwrap();
      toast('Influencer added');
    } catch (e) {
      fail(e, "Couldn't add that influencer");
    }
  };

  const runConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    try {
      await deleteInfluencer(c.id).unwrap();
      toast('Influencer removed');
    } catch (e) {
      fail(e, "Couldn't remove that influencer");
    }
  };

  return (
    <>
      <NotePanel>
        💡 <b>Incremental (marginal) tiers.</b> Each rate applies only to the slice of revenue inside its band — like
        income-tax slabs, not a flat rate on the whole amount. Example with the defaults: <b>10%</b> on the first
        ₹1,00,000, <b>15%</b> on the next ₹2,00,000 (₹1L–₹3L), and <b>20%</b> on everything above ₹3,00,000. Leave the
        last tier's “Up to” blank for “and above”. Edits save on their own a moment after you stop typing.
      </NotePanel>

      {!canEdit && <ReadOnlyNotice role={role} what="commission tiers" />}

      <QueryState query={payoutQuery} skeleton={<InfluencersSkeleton />}>
        {(data) => {
          const byChannel = new Map(data.channels.map((c) => [c.channel, c]));
          const open = byChannel.get(channelId) || { revenue: 0, payout: 0, influencers: [] };
          const list = open.influencers;

          return (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {CH.filter((c) => byChannel.has(c.id)).map((c) => (
                  <ChannelPickerChip
                    key={c.id}
                    channel={c}
                    active={channelId === c.id}
                    revenue={inrShort(byChannel.get(c.id).revenue)}
                    onClick={() => dispatch(setInfluencerChannel(c.id))}
                  />
                ))}
              </Box>

              <KpiGrid
                columns={4}
                items={[
                  { label: 'Channel Revenue', value: inrShort(open.revenue), sub: `${CHMAP[channelId].name} · ${range}d`, accent: CHCOLOR[channelId] },
                  { label: 'Influencers', value: list.length, sub: 'On this channel', accent: 'var(--s3)' },
                  { label: 'Total Payout', value: inrShort(open.payout), sub: `${fmtPct(pct(open.payout, open.revenue))} of channel revenue`, accent: 'var(--gold)' },
                  { label: 'Net To Lab', value: inrShort(open.net), sub: 'Revenue − influencer payout', accent: 'var(--s1)' },
                ]}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', m: '6px 0 12px', gap: 1.5, flexWrap: 'wrap' }}>
                <SectionHeading sx={{ m: 0 }}>{CHMAP[channelId].name} · influencers</SectionHeading>
                {canEdit && <AppButton tone="gold" onClick={add}>＋ Add influencer</AppButton>}
              </Box>

              <Grid container spacing={2}>
                {list.length === 0 ? (
                  <Grid size={12}>
                    <DataCard>No influencers yet. Add one to configure commission tiers.</DataCard>
                  </Grid>
                ) : (
                  list.map((inf) => (
                    // keyed by channel too, so a draft can't survive a channel switch
                    <Grid key={`${channelId}:${inf.id}`} size={{ xs: 12, lg: 6 }}>
                      <InfluencerCard
                        channelId={channelId}
                        influencer={inf}
                        channelRevenue={open.revenue}
                        onSave={save}
                        onRemove={(i) => setConfirm({ id: i.id, name: i.name })}
                        canEdit={canEdit}
                      />
                    </Grid>
                  ))
                )}
              </Grid>
            </>
          );
        }}
      </QueryState>

      <ConfirmDialog
        open={!!confirm}
        tone="danger"
        title="Remove influencer"
        message={confirm ? `Remove “${confirm.name}” and their commission tiers?` : ''}
        detail="Payouts already reported for past windows are unaffected."
        confirmLabel="Remove"
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirm}
      />
    </>
  );
}
